<template>
  <div class="fair-survey-step fair-survey-step--wide">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="results-step__filters">
      <FairDropdown
        v-model="selectedBrand"
        class="results-step__filter"
        :options="brandDropdownOptions"
        placeholder="Все бренды"
        show-radio
      />
      <FairDropdown
        v-model="selectedSystem"
        class="results-step__filter"
        :options="systemDropdownOptions"
        placeholder="Система размеров"
        show-radio
      />
    </div>

    <p
      v-if="isLoadingResults"
      class="fair-survey-step__note"
    >
      Загрузка результатов…
    </p>

    <div
      v-else-if="categories.length"
      class="results-step__grid"
    >
      <article
        v-for="category in categories"
        :key="category.key"
        class="results-step__card"
      >
        <img
          class="results-step__card-image"
          :src="category.image"
          :alt="category.title"
        >
        <span class="results-step__card-title">{{ category.title }}</span>
        <span class="results-step__card-subtitle">{{ category.subtitle }}</span>

        <FairDropdown
          v-model="selectedSizes[category.key]"
          class="results-step__card-size"
          :options="category.sizes.map((size) => ({ key: size, label: size }))"
          placeholder="Размер"
          show-radio
        />
      </article>
    </div>

    <p
      v-else
      class="fair-survey-step__note"
    >
      Результаты пока не загружены — завершите опрос.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FairDropdown } from '@/ui/primitives/FairDropdown'
import { useSurveyModal } from '@/features/survey/store'
import { fetchSurveyResults } from '@/features/survey/api/survey'
import { primarySize } from '@/features/sizes/suggested-size'
import type { SurveyStep } from '@/features/survey/types/survey-config'
import type { CategoryKey, BrandOption, SystemOption } from '@/features/survey/store/useSurveyModalStore'

interface Props {
  step: SurveyStep & { type: 'results' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()
const selectedBrand = ref('all')
const selectedSystem = ref('metric')
const isLoadingResults = ref(false)

// Получаем доступные опции из результатов
const availableBrands = computed<BrandOption[]>(() => {
  const results = surveyStore.surveyModal.results
  if (results?.brands && results.brands.length > 0) {
    return results.brands
  }
  // Fallback значения по умолчанию
  return [
    { value: 'all', label: 'Все бренды' },
    { value: 'zara', label: 'Zara' },
    { value: 'hm', label: 'H&M' },
    { value: 'uniqlo', label: 'Uniqlo' },
    { value: 'nike', label: 'Nike' }
  ]
})

const availableSystems = computed<SystemOption[]>(() => {
  const results = surveyStore.surveyModal.results
  if (results?.systems && results.systems.length > 0) {
    return results.systems
  }
  // Fallback значения по умолчанию
  return [
    { value: 'metric', label: 'Метрическая' },
    { value: 'us', label: 'US' },
    { value: 'eu', label: 'EU' }
  ]
})

// Хранилище для выбранных размеров по категориям
/** FairDropdown keys its options and echoes the key back as the value. */
const brandDropdownOptions = computed(() =>
  availableBrands.value.map((brand) => ({ key: brand.value, label: brand.label })),
)

const systemDropdownOptions = computed(() =>
  availableSystems.value.map((system) => ({
    key: system.value,
    label: system.label,
  })),
)

const selectedSizes = ref<Record<CategoryKey, string>>({
  tops: '',
  bottoms: '',
  dresses: '',
  outerwear: '',
  lingerie: '',
  shoes: ''
})

const CATEGORY_KEYS = ['tops', 'bottoms', 'dresses', 'outerwear', 'lingerie', 'shoes'] as const

/**
 * Кандидаты на предвыбор: sizing v2 suggestedSize, затем легаси-массив.
 * Рантайм-ключ легаси-массива после кейс-трансформера — suggestedSizes,
 * в типе он объявлен как suggested_sizes — читаем оба (reader tolerance).
 */
function suggestedCandidates(item: {
  suggestedSize?: Parameters<typeof primarySize>[0]
  suggested_sizes?: string[]
  suggestedSizes?: string[]
}): string[] {
  const v2 = primarySize(item.suggestedSize)
  const legacy = item.suggested_sizes ?? item.suggestedSizes ?? []
  return v2 ? [v2, ...legacy] : [...legacy]
}

interface CategoryDisplay {
  key: CategoryKey
  image: string
  title: string
  subtitle: string
  sizes: string[]
}

// Преобразуем данные из store в формат для отображения
const categories = computed<CategoryDisplay[]>(() => {
  const rawResults = surveyStore.surveyModal.results
  if (!rawResults) {
    return []
  }

  return CATEGORY_KEYS.map((key) => {
    const item = rawResults[key]
    if (!item) {
      return {
        key,
        image: '',
        title: key.charAt(0).toUpperCase() + key.slice(1),
        subtitle: '',
        sizes: []
      }
    }

    const availableSizes = item.sizes || []

    // Если выбранный размер не установлен или не доступен — предвыбираем
    // рекомендованный (v2 suggestedSize, затем легаси), иначе первый доступный
    if (!selectedSizes.value[key] || !availableSizes.includes(selectedSizes.value[key])) {
      const preferred = suggestedCandidates(item).find((size) => availableSizes.includes(size))
      selectedSizes.value[key] = preferred ?? availableSizes[0] ?? ''
    }

    return {
      key,
      image: item.image,
      title: item.title,
      subtitle: item.description,
      sizes: availableSizes
    }
  })
})

// Инициализируем выбранные размеры из suggested_sizes при загрузке результатов
watch(
  () => surveyStore.surveyModal.results,
  (results) => {
    if (results) {
      CATEGORY_KEYS.forEach((key) => {
        const item = results[key]
        if (item) {
          const availableSizes = item.sizes || []

          // Если выбранный размер не установлен — инициализируем рекомендованным
          if (!selectedSizes.value[key] || !availableSizes.includes(selectedSizes.value[key])) {
            const preferred = suggestedCandidates(item).find((size) => availableSizes.includes(size))
            const fallback = preferred ?? availableSizes[0]
            if (fallback) {
              selectedSizes.value[key] = fallback
            }
          }
        }
      })
    }
  },
  { immediate: true }
)

const loadResults = async () => {
  isLoadingResults.value = true
  try {
    const surveyResponseId = surveyStore.surveyModal.surveyResponseId
    if (!surveyResponseId) {
      // Если результатов еще нет, пытаемся отправить опрос
      if (surveyStore.surveyModal.config) {
        await surveyStore.submitSurvey()
        // После отправки surveyResponseId должен быть установлен
        const newResponseId = surveyStore.surveyModal.surveyResponseId
        if (newResponseId) {
          const results = await fetchSurveyResults({
            surveyResponseId: newResponseId,
            brand: selectedBrand.value,
            system: selectedSystem.value
          })
          surveyStore.surveyModal.results = results
        }
      }
      return
    }

    // Запрашиваем данные результатов с бекенда с параметрами бренда и системы измерения
    const results = await fetchSurveyResults({
      surveyResponseId,
      brand: selectedBrand.value,
      system: selectedSystem.value
    })
    
    // Сохраняем результаты в store
    surveyStore.surveyModal.results = results
  } catch (error) {
    console.error('Error loading survey results:', error)
  } finally {
    isLoadingResults.value = false
  }
}

// Отслеживаем изменения бренда и системы измерения
watch([selectedBrand, selectedSystem], () => {
  // При изменении бренда или системы измерения делаем новый запрос
  if (surveyStore.surveyModal.surveyResponseId) {
    loadResults()
  }
})

onMounted(() => {
  // Загружаем результаты при монтировании компонента
  loadResults()
})
</script>

<style scoped>
.results-step__filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
}

.results-step__filter {
  min-width: 220px;
}

.results-step__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--space-16);
}

.results-step__card {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  padding: var(--space-16);
  border: 1px solid var(--color-line-hairline);
  border-radius: var(--radius-card);
}

/* Category art is a small pictogram — upscaling it only blurs it. */
.results-step__card-image {
  width: 44px;
  height: 60px;
  object-fit: contain;
}

.results-step__card-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.results-step__card-subtitle {
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 13px;
  line-height: 1.4;
  color: var(--color-text-muted);
}

.results-step__card-size {
  margin-top: auto;
  padding-top: var(--space-8);
}
</style>
